import { ActionFunctionArgs, redirect } from "@remix-run/cloudflare";
import { Link, useSearchParams } from "@remix-run/react";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { ValidatedForm, validationError } from "remix-validated-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { v4 } from "uuid";
import { getDbFromContext } from "@/lib/db.service.server";
import { users } from "@/lib/schema";
import { eq } from "drizzle-orm";
import { emailService } from "@/lib/email.service";

const validator = withZod(
  z.object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email("Must be a valid email")
  })
);

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.clone().formData();
  const result = await validator.validate(formData);
  if (result.error) {
    throw validationError(result.error);
  }

  const { email } = result.data;
  const db = getDbFromContext(context);

  const token = v4();
  const user = await db.select().from(users).where(eq(users.email, email)).get();
  if (user) {
    const update = await db.update(users).set({
      emailConfirmKey: token
    }).where(eq(users.email, email)).run();

    await emailService.sendTemplate(email, {
      action_url: `https://rank-hub.com/reset-password/${email}/${token}`
    }, "password-reset", context);
  }

  return redirect(`/reset-password/sent?email=${email}`);
}

export default function Index() {
  const [params] = useSearchParams();

  return (
    <div
      className="min-h-screen relative md:flex bg-gray-900">
      <div className="hidden md:block relative md:flex-grow-[1.618] w-0">
        <div className="absolute inset-0 pricing-bg" />
        <div className="relative z-10 flex items-center justify-center text-white h-full p-8">
          <h1 className="text-4xl font-bold text-white max-w-[40rem] text-center leading-[1.35]">
            Start Generating Targeted Traffic that Moves the Needle
          </h1>
        </div>
      </div>
      <div className="sm:p-16 p-6 pt-12 md:flex-grow md:w-0 flex items-center justify-center bg-white">
        <div className="flex w-full flex-col max-w-sm gap-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight">
              Reset your password
            </h1>
          </div>
          <ValidatedForm validator={validator} method="post" defaultValues={{
            email: params.get("email") ?? ""
          }} className="">
            <div className="flex flex-col gap-1">
              <Input name="email" label="Email" />
              <Button submit className="mt-4 flex">
                <Send size="20" />
                Send Reset Link
              </Button>
            </div>
            <div className="flex mt-2 gap-2 items-center justify-center">
              <Link
                className="text-sm underline underline-offset-4 text-center block"
                to="/login"
              >
                Back to login
              </Link>
              <span className="text-gray-600 text-sm">•</span>
              <Link
                className="text-sm underline underline-offset-4 text-center block"
                to="/signup"
              >
                Signup instead
              </Link>
            </div>
          </ValidatedForm>
        </div>
      </div>
    </div>
  );
}
